import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { NAV_ONLY, ROUTES, permissionsFor } from '../../routes/routeCatalogue';
import {
    filterSidebarSections,
    resolveSidebarWorkspace,
    type MenuItem,
    type SectionGroup,
    type SubMenuItem,
} from './sidebarAccess';
import SidebarFlyout from './SidebarFlyout';
import {
    LayoutDashboard,
    Users,
    UserCircle,
    CreditCard,
    Wallet,
    Landmark,
    Car,
    PieChart,
    BarChart3,
    Settings,
    ShieldCheck,
    FileText,
    FileSearch,
    Phone,
    LayoutGrid,
    DollarSign,
    AlertTriangle,
    Calendar,
    ClipboardCheck,
    Compass,
    IdCard,
    BookOpen,
    Store,
    Banknote,
    Trophy,
    Lightbulb,
    Gift,
    Star,
    Layers,
    Bell,
    Brain,
    Network,
    Globe,
    Boxes,
    ChevronDown,
    ChevronRight,
    PanelLeftClose,
    PanelLeftOpen,
    Home,
    Container,
    ListOrdered,
    Search,
    Activity,
    Database,
    GitBranch,
} from 'lucide-react';
import './Sidebar.css';

const STORAGE_EXPANDED = 'sidebar:expandedMenus';
const STORAGE_SECTIONS = 'sidebar:expandedSections';

export function buildSections(): SectionGroup[] {
    return [
        {
            key: 'customer-ops',
            label: 'Customer Operations',
            icon: <Users size={16} />,
            workspace: 'institution',
            items: [
                { path: ROUTES.CUSTOMERS.path, label: 'Retail Users', icon: <UserCircle size={18} />, requiredPermissions: permissionsFor(ROUTES.CUSTOMERS) },
                { path: ROUTES.TRANSACTIONS.path, label: 'Transactions', icon: <CreditCard size={18} />, requiredPermissions: permissionsFor(ROUTES.TRANSACTIONS) },
                { path: ROUTES.DEPOSITS_PRODUCTS.path, label: 'Deposits', icon: <Wallet size={18} />, requiredPermissions: permissionsFor(ROUTES.DEPOSITS_PRODUCTS) },
                {
                    key: 'openbanking-advanced',
                    label: 'Open Banking',
                    icon: <Globe size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: permissionsFor(NAV_ONLY.OPENBANKING_HOME),
                    children: [
                        { path: NAV_ONLY.OPENBANKING_HOME.path, label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: permissionsFor(NAV_ONLY.OPENBANKING_HOME) },
                        { path: NAV_ONLY.OPENBANKING_BRANDS.path, label: 'Brands (14)', icon: <Landmark size={16} />, requiredPermissions: permissionsFor(NAV_ONLY.OPENBANKING_BRANDS) },
                        { path: NAV_ONLY.OPENBANKING_TRANSACTIONS.path, label: 'Transactions', icon: <CreditCard size={16} />, requiredPermissions: permissionsFor(NAV_ONLY.OPENBANKING_TRANSACTIONS) },
                        { path: NAV_ONLY.OPENBANKING_WEBHOOKS.path, label: 'Webhooks', icon: <Network size={16} />, requiredPermissions: permissionsFor(NAV_ONLY.OPENBANKING_WEBHOOKS) },
                        { path: NAV_ONLY.OPENBANKING_ANALYTICS.path, label: 'Analytics', icon: <BarChart3 size={16} />, requiredPermissions: permissionsFor(NAV_ONLY.OPENBANKING_ANALYTICS) },
                    ],
                },
                {
                    key: 'loans',
                    label: 'Loans',
                    icon: <Car size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: permissionsFor(ROUTES.LOANS),
                    children: [
                        { path: ROUTES.LOANS.path, label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: permissionsFor(ROUTES.LOANS) },
                        { path: ROUTES.LOANS_PIPELINE.path, label: 'Pipeline', icon: <LayoutGrid size={16} />, requiredPermissions: permissionsFor(ROUTES.LOANS_PIPELINE) },
                        { path: ROUTES.LOANS_APPLICATIONS.path, label: 'Applications', icon: <FileText size={16} />, requiredPermissions: permissionsFor(ROUTES.LOANS_APPLICATIONS) },
                        { path: ROUTES.LOANS_CREDIT_DECISIONING.path, label: 'Credit Decisioning', icon: <ShieldCheck size={16} />, requiredPermissions: permissionsFor(ROUTES.LOANS_CREDIT_DECISIONING) },
                        { path: ROUTES.LOANS_DISBURSEMENT.path, label: 'Disbursement', icon: <DollarSign size={16} />, requiredPermissions: permissionsFor(ROUTES.LOANS_DISBURSEMENT) },
                        { path: ROUTES.LOANS_ACCOUNTS.path, label: 'Accounts', icon: <BookOpen size={16} />, requiredPermissions: permissionsFor(ROUTES.LOANS_ACCOUNTS) },
                        { path: ROUTES.LOANS_COLLECTIONS.path, label: 'Collections', icon: <Phone size={16} />, requiredPermissions: permissionsFor(ROUTES.LOANS_COLLECTIONS) },
                        { path: ROUTES.LOANS_PORTFOLIO.path, label: 'Portfolio Risk', icon: <PieChart size={16} />, requiredPermissions: permissionsFor(ROUTES.LOANS_PORTFOLIO) },
                        { path: ROUTES.LOANS_PRODUCTS.path, label: 'Products', icon: <LayoutGrid size={16} />, requiredPermissions: permissionsFor(ROUTES.LOANS_PRODUCTS) },
                        { path: ROUTES.LOANS_REPORTS.path, label: 'Reports', icon: <BarChart3 size={16} />, requiredPermissions: permissionsFor(ROUTES.LOANS_REPORTS) },
                    ],
                },
                {
                    key: 'mortgages',
                    label: 'Mortgages',
                    icon: <Home size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: permissionsFor(ROUTES.MORTGAGES_PIPELINE),
                    children: [
                        { path: ROUTES.MORTGAGES_PIPELINE.path, label: 'Pipeline', icon: <LayoutDashboard size={16} />, requiredPermissions: permissionsFor(ROUTES.MORTGAGES_PIPELINE) },
                        { path: ROUTES.MORTGAGES_PRODUCTS.path, label: 'Products', icon: <LayoutGrid size={16} />, requiredPermissions: permissionsFor(ROUTES.MORTGAGES_PRODUCTS) },
                        { path: ROUTES.MORTGAGES_AFFORDABILITY.path, label: 'Affordability', icon: <BarChart3 size={16} />, requiredPermissions: permissionsFor(ROUTES.MORTGAGES_AFFORDABILITY) },
                        { path: ROUTES.MORTGAGES_REPORTS.path, label: 'Reports', icon: <PieChart size={16} />, requiredPermissions: permissionsFor(ROUTES.MORTGAGES_REPORTS) },
                    ],
                },
                {
                    key: 'insurance',
                    label: 'Insurance',
                    icon: <ShieldCheck size={18} />,
                    hasSubmenu: true,
                    children: [
                        { path: ROUTES.ADMIN_INSURANCE_PRODUCTS.path, label: 'Products', icon: <LayoutGrid size={16} />, requiredPermissions: permissionsFor(ROUTES.ADMIN_INSURANCE_PRODUCTS) },
                        { path: ROUTES.ADMIN_INSURANCE_QUOTES.path, label: 'Quotes', icon: <FileText size={16} />, requiredPermissions: permissionsFor(ROUTES.ADMIN_INSURANCE_QUOTES) },
                        { path: ROUTES.ADMIN_INSURANCE_UNDERWRITING.path, label: 'Underwriting', icon: <ClipboardCheck size={16} />, requiredPermissions: permissionsFor(ROUTES.ADMIN_INSURANCE_UNDERWRITING) },
                        { path: ROUTES.ADMIN_INSURANCE_CLAIMS.path, label: 'Claims', icon: <AlertTriangle size={16} />, requiredPermissions: permissionsFor(ROUTES.ADMIN_INSURANCE_CLAIMS) },
                    ],
                },
                {
                    key: 'payment-ops',
                    label: 'Payment Ops',
                    icon: <Landmark size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: permissionsFor(ROUTES.PAYMENT_OPS),
                    children: [
                        { path: ROUTES.PAYMENT_OPS.path, label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: permissionsFor(ROUTES.PAYMENT_OPS) },
                        { path: ROUTES.PAYMENT_OPS_SEARCH.path, label: 'Payment Search', icon: <Search size={16} />, requiredPermissions: permissionsFor(ROUTES.PAYMENT_OPS_SEARCH) },
                        { path: ROUTES.PAYMENT_OPS_RECONCILIATION.path, label: 'Reconciliation', icon: <BarChart3 size={16} />, requiredPermissions: permissionsFor(ROUTES.PAYMENT_OPS_RECONCILIATION) },
                        { path: ROUTES.PAYMENT_OPS_STANDING_ORDERS.path, label: 'Standing Orders', icon: <FileText size={16} />, requiredPermissions: permissionsFor(ROUTES.PAYMENT_OPS_STANDING_ORDERS) },
                        { path: ROUTES.PAYMENT_OPS_FAILED.path, label: 'Failed Payments', icon: <Banknote size={16} />, requiredPermissions: permissionsFor(ROUTES.PAYMENT_OPS_FAILED) },
                        { path: ROUTES.PAYMENT_OPS_SCHEME_ROUTING.path, label: 'Scheme Routing', icon: <Network size={16} />, requiredPermissions: permissionsFor(ROUTES.PAYMENT_OPS_SCHEME_ROUTING) },
                    ],
                },
                {
                    key: 'general-ledger',
                    label: 'General Ledger',
                    icon: <BookOpen size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: permissionsFor(ROUTES.GENERAL_LEDGER),
                    children: [
                        { path: ROUTES.GENERAL_LEDGER.path, label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: permissionsFor(ROUTES.GENERAL_LEDGER) },
                        { path: ROUTES.GENERAL_LEDGER_ACCOUNTS.path, label: 'Chart of Accounts', icon: <BookOpen size={16} />, requiredPermissions: permissionsFor(ROUTES.GENERAL_LEDGER_ACCOUNTS) },
                        { path: ROUTES.GENERAL_LEDGER_JOURNALS.path, label: 'Journal Entries', icon: <FileText size={16} />, requiredPermissions: permissionsFor(ROUTES.GENERAL_LEDGER_JOURNALS) },
                        { path: ROUTES.GENERAL_LEDGER_TRIAL_BALANCE.path, label: 'Trial Balance', icon: <BarChart3 size={16} />, requiredPermissions: permissionsFor(ROUTES.GENERAL_LEDGER_TRIAL_BALANCE) },
                    ],
                },
            ],
        },
        {
            key: 'branch-banking',
            label: 'Branch Banking',
            icon: <Store size={16} />,
            workspace: 'institution',
            items: [
                {
                    key: 'branch',
                    label: 'Branch Banking',
                    icon: <Store size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: permissionsFor(ROUTES.BRANCH_DASHBOARD),
                    children: [
                        { path: ROUTES.BRANCH_DASHBOARD.path, label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: permissionsFor(ROUTES.BRANCH_DASHBOARD) },
                        { path: ROUTES.BRANCH_CUSTOMERS.path, label: 'Customer Lookup', icon: <UserCircle size={16} />, requiredPermissions: permissionsFor(ROUTES.BRANCH_CUSTOMERS) },
                        { path: ROUTES.BRANCH_ACCOUNTS_OPEN.path, label: 'Account Opening', icon: <Container size={16} />, requiredPermissions: permissionsFor(ROUTES.BRANCH_ACCOUNTS_OPEN) },
                        { path: ROUTES.BRANCH_TRANSACTIONS.path, label: 'Transactions', icon: <Banknote size={16} />, requiredPermissions: permissionsFor(ROUTES.BRANCH_TRANSACTIONS) },
                        { path: ROUTES.BRANCH_CASH.path, label: 'Cash Management', icon: <DollarSign size={16} />, requiredPermissions: permissionsFor(ROUTES.BRANCH_CASH) },
                        { path: ROUTES.BRANCH_QUEUE.path, label: 'Queue', icon: <ListOrdered size={16} />, requiredPermissions: permissionsFor(ROUTES.BRANCH_QUEUE) },
                    ],
                },
            ],
        },
        {
            key: 'compliance-risk',
            label: 'Compliance & Risk',
            icon: <ShieldCheck size={16} />,
            workspace: 'institution',
            items: [
                {
                    key: 'kyc',
                    label: 'KYC Management',
                    icon: <ClipboardCheck size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: permissionsFor(ROUTES.KYC),
                    children: [
                        { path: ROUTES.KYC.path, label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: permissionsFor(ROUTES.KYC) },
                        { path: ROUTES.KYC_REVIEWS.path, label: 'Reviews', icon: <ClipboardCheck size={16} />, requiredPermissions: permissionsFor(ROUTES.KYC_REVIEWS) },
                        { path: ROUTES.KYC_ANALYTICS.path, label: 'Analytics', icon: <BarChart3 size={16} />, requiredPermissions: permissionsFor(ROUTES.KYC_ANALYTICS) },
                    ],
                },
                {
                    key: 'compliance',
                    label: 'Compliance',
                    icon: <ShieldCheck size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: permissionsFor(ROUTES.COMPLIANCE),
                    children: [
                        { path: ROUTES.COMPLIANCE.path, label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: permissionsFor(ROUTES.COMPLIANCE) },
                        { path: ROUTES.COMPLIANCE_ALERTS.path, label: 'Alert Management', icon: <AlertTriangle size={16} />, requiredPermissions: permissionsFor(ROUTES.COMPLIANCE_ALERTS) },
                        { path: ROUTES.COMPLIANCE_SAR.path, label: 'SAR Filing', icon: <FileText size={16} />, requiredPermissions: permissionsFor(ROUTES.COMPLIANCE_SAR) },
                        { path: ROUTES.COMPLIANCE_REGULATORY_RETURNS.path, label: 'Regulatory Returns', icon: <Calendar size={16} />, requiredPermissions: permissionsFor(ROUTES.COMPLIANCE_REGULATORY_RETURNS) },
                        { path: ROUTES.COMPLIANCE_SANCTIONS.path, label: 'Sanctions Screening', icon: <ClipboardCheck size={16} />, requiredPermissions: permissionsFor(ROUTES.COMPLIANCE_SANCTIONS) },
                    ],
                },
                {
                    key: 'audit',
                    label: 'Audit Trail',
                    icon: <FileSearch size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: permissionsFor(ROUTES.AUDIT),
                    children: [
                        { path: ROUTES.AUDIT.path, label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: permissionsFor(ROUTES.AUDIT) },
                        { path: ROUTES.AUDIT_LOG.path, label: 'Event Log', icon: <FileText size={16} />, requiredPermissions: permissionsFor(ROUTES.AUDIT_LOG) },
                        { path: ROUTES.AUDIT_USERS.path, label: 'User Activity', icon: <Users size={16} />, requiredPermissions: permissionsFor(ROUTES.AUDIT_USERS) },
                    ],
                },
            ],
        },
        {
            key: 'products-config',
            label: 'Products & Config',
            icon: <Boxes size={16} />,
            workspace: 'institution',
            items: [
                { path: ROUTES.SMART_PANEL.path, label: 'Smart Panel', icon: <Bell size={18} />, requiredPermissions: permissionsFor(ROUTES.SMART_PANEL) },
            ],
        },
        {
            key: 'ai-intelligence',
            label: 'AI & Intelligence',
            icon: <Brain size={16} />,
            workspace: 'institution',
            items: [
                { path: ROUTES.INSIGHTS.path, label: 'Insights', icon: <Lightbulb size={18} />, requiredPermissions: permissionsFor(ROUTES.INSIGHTS) },
                { path: ROUTES.PROFILE_TRAITS.path, label: 'Profile Traits', icon: <IdCard size={18} />, requiredPermissions: permissionsFor(ROUTES.PROFILE_TRAITS) },
                { path: ROUTES.ADMIN_MONI_AI_FEEDBACK.path, label: 'Moni AI Feedback', icon: <AlertTriangle size={18} />, requiredPermissions: permissionsFor(ROUTES.ADMIN_MONI_AI_FEEDBACK) },
                {
                    key: 'rewards',
                    label: 'Rewards',
                    icon: <Trophy size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: permissionsFor(ROUTES.REWARDS_CONFIG),
                    children: [
                        { path: ROUTES.REWARDS_CONFIG.path, label: 'Configuration', icon: <Settings size={16} />, requiredPermissions: permissionsFor(ROUTES.REWARDS_CONFIG) },
                        { path: ROUTES.REWARDS_BADGES.path, label: 'Badges', icon: <Star size={16} />, requiredPermissions: permissionsFor(ROUTES.REWARDS_BADGES) },
                        { path: ROUTES.REWARDS_TIERS.path, label: 'Tiers', icon: <Layers size={16} />, requiredPermissions: permissionsFor(ROUTES.REWARDS_TIERS) },
                        { path: ROUTES.REWARDS_OFFERS.path, label: 'Offers', icon: <Gift size={16} />, requiredPermissions: permissionsFor(ROUTES.REWARDS_OFFERS) },
                        { path: ROUTES.REWARDS_ANALYTICS.path, label: 'Analytics', icon: <PieChart size={16} />, requiredPermissions: permissionsFor(ROUTES.REWARDS_ANALYTICS) },
                    ],
                },
                { path: ROUTES.JOURNEY_TEMPLATES.path, label: 'Journeys', icon: <Compass size={18} />, requiredPermissions: permissionsFor(ROUTES.JOURNEY_TEMPLATES) },
            ],
        },
        {
            key: 'platform',
            label: 'Platform',
            icon: <Globe size={16} />,
            workspace: 'platform',
            items: [
                { path: ROUTES.API_MANAGEMENT.path, label: 'API Management', icon: <Network size={18} />, requiredPermissions: permissionsFor(ROUTES.API_MANAGEMENT) },
                { path: ROUTES.GATEWAY_ROUTES.path, label: 'Gateway Routes', icon: <GitBranch size={18} />, requiredPermissions: permissionsFor(ROUTES.GATEWAY_ROUTES) },
                { path: ROUTES.PLATFORM_HEALTH.path, label: 'System Health', icon: <Activity size={18} />, requiredPermissions: permissionsFor(ROUTES.PLATFORM_HEALTH) },
                { path: ROUTES.PLATFORM_METERING.path, label: 'Metering & Billing', icon: <BarChart3 size={18} />, requiredPermissions: permissionsFor(ROUTES.PLATFORM_METERING) },
                { path: ROUTES.PLATFORM_MIGRATION.path, label: 'Migration Console', icon: <Database size={18} />, requiredPermissions: permissionsFor(ROUTES.PLATFORM_MIGRATION) },
            ],
        },
        {
            key: 'administration',
            label: 'Administration',
            icon: <Settings size={16} />,
            workspace: 'shared',
            items: [
                { path: ROUTES.USERS.path, label: 'Users', icon: <Users size={18} />, requiredPermissions: permissionsFor(ROUTES.USERS) },
                { path: ROUTES.INSTITUTIONS.path, label: 'Institutions', icon: <Landmark size={18} />, requiredPermissions: permissionsFor(ROUTES.INSTITUTIONS), workspace: 'platform' },
                { path: ROUTES.SETTINGS.path, label: 'Settings', icon: <Settings size={18} />, requiredPermissions: permissionsFor(ROUTES.SETTINGS) },
            ],
        },
    ];
}

interface FlyoutState {
    item: MenuItem;
    anchorElement: HTMLButtonElement;
}

const Sidebar: React.FC = () => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(() => {
        const saved = localStorage.getItem(STORAGE_EXPANDED);
        return saved ? JSON.parse(saved) : {};
    });
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
        const saved = localStorage.getItem(STORAGE_SECTIONS);
        return saved ? JSON.parse(saved) : {};
    });
    const [flyout, setFlyout] = useState<FlyoutState | null>(null);

    const location = useLocation();
    const navigate = useNavigate();
    const {
        effectiveRole,
        hasAnyPermission,
        originalUser,
        permissionsLoading,
        user,
    } = useAuth();
    const originalRole = originalUser?.role ?? user?.originalRole ?? user?.role;
    const workspace = resolveSidebarWorkspace(effectiveRole);

    // Persist state
    useEffect(() => { localStorage.setItem(STORAGE_EXPANDED, JSON.stringify(expandedMenus)); }, [expandedMenus]);
    useEffect(() => { localStorage.setItem(STORAGE_SECTIONS, JSON.stringify(expandedSections)); }, [expandedSections]);
    useEffect(() => {
        document.documentElement.dataset.sidebarState = isCollapsed ? 'collapsed' : 'expanded';
        return () => {
            delete document.documentElement.dataset.sidebarState;
        };
    }, [isCollapsed]);

    // Auto-expand section + submenu when navigating to a deep route
    useEffect(() => {
        const sections = buildSections();
        for (const section of sections) {
            for (const item of section.items) {
                if (item.hasSubmenu && item.key && item.children) {
                    const match = item.children.some(c => location.pathname.startsWith(c.path));
                    if (match) {
                        setExpandedSections(prev => {
                            if (prev[section.key]) return prev;
                            return { ...prev, [section.key]: true };
                        });
                        setExpandedMenus(prev => {
                            if (prev[item.key!]) return prev;
                            return { ...prev, [item.key!]: true };
                        });
                    }
                }
            }
        }
    }, [location.pathname]);

    // Responsive: auto-collapse on tablet
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 1024px)');
        const handler = (e: MediaQueryListEvent) => { if (e.matches) setIsCollapsed(true); };
        mq.addEventListener('change', handler);
        if (mq.matches) setIsCollapsed(true);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const closeFlyout = useCallback(() => setFlyout(null), []);
    useEffect(() => {
        closeFlyout();
    }, [closeFlyout, location.pathname]);

    useEffect(() => {
        if (!isCollapsed) closeFlyout();
    }, [closeFlyout, isCollapsed]);

    const handleToggle = useCallback(() => {
        closeFlyout();
        setIsCollapsed(prev => !prev);
    }, [closeFlyout]);

    const toggleSubmenu = useCallback((menuKey: string) => {
        setExpandedMenus(prev => ({ ...prev, [menuKey]: !prev[menuKey] }));
    }, []);

    const toggleSection = useCallback((sectionKey: string) => {
        setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    }, []);

    // Build filtered sections
    const sections = useMemo(
        () => permissionsLoading
            ? []
            : filterSidebarSections(buildSections(), {
                workspace,
                originalRole,
                hasAnyPermission,
            }),
        [hasAnyPermission, originalRole, permissionsLoading, workspace],
    );

    // Active-state helpers
    const isItemActive = (item: MenuItem): boolean => {
        if (item.hasSubmenu && item.children) {
            return item.children.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'));
        }
        if (item.path === ROUTES.GATEWAY_ROUTES.path) return location.pathname.startsWith(ROUTES.GATEWAY_ROUTES.path);
        return location.pathname === item.path;
    };

    const isSubmenuActive = (child: SubMenuItem): boolean => location.pathname === child.path;
    const isMenuExpanded = (item: MenuItem): boolean => Boolean(
        item.hasSubmenu &&
        item.key &&
        (isCollapsed
            ? flyout?.item.key === item.key
            : expandedMenus[item.key]),
    );

    const sidebarToggleLabel = isCollapsed ? 'Keep sidebar open' : 'Collapse sidebar';

    return (
        <>
            <aside className={`sidebar ${isCollapsed ? 'sidebar--collapsed' : 'sidebar--expanded'}`} role="navigation" aria-label="Main navigation">
            {/* Header */}
            <div className="sidebar__header">
                <button
                    className="sidebar__toggle"
                    onClick={handleToggle}
                    title={sidebarToggleLabel}
                    aria-label={sidebarToggleLabel}
                    aria-pressed={!isCollapsed}
                    type="button"
                >
                    {isCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
                </button>
            </div>

            <nav className="sidebar__nav">
                {/* Dashboard — always visible */}
                <div className="sidebar__section">
                    <button
                        className={`sidebar__menu-item ${location.pathname === ROUTES.DASHBOARD.path || location.pathname === ROUTES.ROOT.path ? 'sidebar__menu-item--active' : ''}`}
                        role="menuitem"
                        aria-current={location.pathname === ROUTES.DASHBOARD.path ? 'page' : undefined}
                        aria-label="Dashboard"
                        onClick={() => navigate(ROUTES.DASHBOARD.path)}
                        title={isCollapsed ? 'Dashboard' : ''}
                    >
                        <span className="sidebar__menu-icon"><LayoutDashboard size={18} /></span>
                        {!isCollapsed && <span className="sidebar__menu-label">Dashboard</span>}
                    </button>
                </div>

                {/* Section groups */}
                {sections.map(section => {
                    const isSectionOpen = expandedSections[section.key] !== false; // default open
                    return (
                        <div key={section.key} className="sidebar__section">
                            {!isCollapsed && (
                                <button
                                    className="sidebar__section-header"
                                    onClick={() => toggleSection(section.key)}
                                    aria-expanded={isSectionOpen}
                                >
                                    <span className="sidebar__section-icon">{section.icon}</span>
                                    <span className="sidebar__section-label">{section.label}</span>
                                    {isSectionOpen
                                        ? <ChevronDown size={14} className="sidebar__section-chevron" />
                                        : <ChevronRight size={14} className="sidebar__section-chevron" />}
                                </button>
                            )}

                            {(isCollapsed || isSectionOpen) && section.items.map(item => (
                                <div key={item.key || item.path}>
                                    <button
                                        className={`sidebar__menu-item ${isItemActive(item) ? 'sidebar__menu-item--active' : ''}`}
                                        role="menuitem"
                                        aria-current={isItemActive(item) && !item.hasSubmenu ? 'page' : undefined}
                                        aria-expanded={item.hasSubmenu ? isMenuExpanded(item) : undefined}
                                        aria-haspopup={item.hasSubmenu ? 'menu' : undefined}
                                        aria-label={item.hasSubmenu ? `${item.label} menu` : item.label}
                                        onClick={(event) => {
                                            if (item.hasSubmenu && item.key) {
                                                if (isCollapsed) {
                                                    setFlyout(current => current?.item.key === item.key
                                                        ? null
                                                        : {
                                                            item,
                                                            anchorElement: event.currentTarget,
                                                        });
                                                } else {
                                                    toggleSubmenu(item.key);
                                                }
                                            } else if (item.path) {
                                                closeFlyout();
                                                navigate(item.path);
                                            }
                                        }}
                                        title={isCollapsed
                                            ? item.hasSubmenu
                                                ? `${item.label} menu`
                                                : item.label
                                            : ''}
                                    >
                                        <span className="sidebar__menu-icon">{item.icon}</span>
                                        {isCollapsed && item.hasSubmenu && (
                                            <span className="sidebar__group-indicator" aria-hidden="true">
                                                <ChevronRight />
                                            </span>
                                        )}
                                        {!isCollapsed && (
                                            <>
                                                <span className="sidebar__menu-label">{item.label}</span>
                                                {item.hasSubmenu && (
                                                    <span
                                                        className={`sidebar__submenu-toggle ${item.key && expandedMenus[item.key] ? 'sidebar__submenu-toggle--expanded' : ''}`}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (item.key) toggleSubmenu(item.key);
                                                        }}
                                                    >
                                                        <ChevronRight size={14} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </button>

                                    {/* Submenu children */}
                                    {item.hasSubmenu && !isCollapsed && item.key && expandedMenus[item.key] && item.children && (
                                        <div className="sidebar__submenu">
                                            {item.children.map(child => (
                                                <button
                                                    key={child.path}
                                                    className={`sidebar__submenu-item ${isSubmenuActive(child) ? 'sidebar__submenu-item--active' : ''}`}
                                                    role="menuitem"
                                                    aria-current={isSubmenuActive(child) ? 'page' : undefined}
                                                    onClick={() => navigate(child.path)}
                                                >
                                                    <span className="sidebar__submenu-icon">{child.icon}</span>
                                                    <span className="sidebar__menu-label">{child.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                })}
            </nav>

            {!isCollapsed && (
                <div className="sidebar__footer">
                    <div className="sidebar__version">Version 2.0.0</div>
                </div>
            )}
            </aside>
            {isCollapsed && flyout && (
                <SidebarFlyout
                    item={flyout.item}
                    anchorElement={flyout.anchorElement}
                    activePath={location.pathname}
                    onDismiss={closeFlyout}
                    onNavigate={(path) => {
                        closeFlyout();
                        navigate(path);
                    }}
                />
            )}
        </>
    );
};

export default Sidebar;
